export const ADMIN_ORGANIZATION_ROLES = ['cdim-admin', 'noc-admin'] as const;
export const ADMIN_CONTACT_ROLES = ['cdim-admin', 'noc-admin'] as const;
export const ADMIN_ROLE_ROLES = ['cdim-admin', 'noc-admin'] as const;
export const ADMIN_EVENT_HUB_ROLES = ['cdim-admin', 'noc-admin'] as const;
export const ADMIN_LOCATION_ROLES = ['admin', 'cdim-admin', 'noc-admin'] as const;
export const ADMIN_INCIDENT_ROLES = ['admin', 'cdim-admin', 'noc-admin'] as const;
export const ADMIN_ACCESS_CARD_READ = 'accesscard-read';
export const ADMIN_ACCESS_CARD_WRITE = 'accesscard-write';

export type AdminRoleContext = {
    userCPRoles: readonly string[];
    organizationSchemeRoles: readonly string[];
};

const ADMIN_ONLY_PATH_PREFIXES = [
    '/users',
    '/organization',
    '/established-contacts',
    '/contact-types',
    '/roles',
    '/event-hub',
    '/visitors',
    '/infrastructure',
    '/incidents',
    '/access-cards',
] as const;

type AdminMenuLink = {
    title: string;
    to: string;
    allowedRoles: readonly string[];
    requiredOrganizationSchemeRoles?: readonly string[];
};

type AdminMenuSection = {
    icon: string;
    category?: string;
    links: AdminMenuLink[];
};

const ADMIN_MENU_SECTIONS: AdminMenuSection[] = [
    {
        icon: 'i-organization',
        links: [{ title: 'Organizations', to: 'organizations', allowedRoles: ADMIN_ORGANIZATION_ROLES }],
    },
    {
        category: 'Account management',
        icon: 'i-manage',
        links: [
            { title: 'Users', to: 'users', allowedRoles: ADMIN_ORGANIZATION_ROLES },
            { title: 'Visitors', to: 'visitors', allowedRoles: ADMIN_ORGANIZATION_ROLES },
        ],
    },
    {
        category: 'Contacts',
        icon: 'i-id-card',
        links: [
            { title: 'Established', to: 'contacts', allowedRoles: ADMIN_CONTACT_ROLES },
            { title: 'Types', to: 'contact-types', allowedRoles: ADMIN_CONTACT_ROLES },
        ],
    },
    {
        category: 'Roles',
        icon: 'i-suitcase',
        links: [{ title: 'Organization roles', to: 'organization-roles', allowedRoles: ADMIN_ROLE_ROLES }],
    },
    {
        icon: 'i-incident',
        links: [{ title: 'Incidents', to: 'noc-incidents', allowedRoles: ADMIN_INCIDENT_ROLES }],
    },
    {
        icon: 'i-headset',
        links: [{ title: 'Event hub', to: 'event-hub', allowedRoles: ADMIN_EVENT_HUB_ROLES }],
    },
    {
        icon: 'i-infrastructure',
        links: [{ title: 'Locations', to: 'locations', allowedRoles: ADMIN_LOCATION_ROLES }],
    },
    {
        icon: 'i-card',
        links: [
            {
                title: 'Access Cards',
                to: 'access-cards',
                allowedRoles: [],
                requiredOrganizationSchemeRoles: [ADMIN_ACCESS_CARD_READ],
            },
        ],
    },
];

function canAccessAdminLink(roleContext: AdminRoleContext, link: AdminMenuLink) {
    if (link.requiredOrganizationSchemeRoles?.length) {
        return link.requiredOrganizationSchemeRoles.some((role) => roleContext.organizationSchemeRoles.includes(role));
    }

    const allowedRoles = link.allowedRoles;
    if (allowedRoles.length === 0) return true;

    return (
        roleContext.userCPRoles.includes('admin') || allowedRoles.some((role) => roleContext.userCPRoles.includes(role))
    );
}

export function getVisibleAdminMenuSections(roleContext: AdminRoleContext) {
    return ADMIN_MENU_SECTIONS.map((section) => ({
        ...section,
        links: section.links
            .filter((link) => canAccessAdminLink(roleContext, link))
            .map((link) => ({ title: link.title, to: link.to })),
    })).filter((section) => section.links.length > 0);
}

export function hasAdminMenuAccess(roleContext: AdminRoleContext) {
    return getVisibleAdminMenuSections(roleContext).length > 0;
}

export function getDefaultAdminRouteName(roleContext: AdminRoleContext) {
    return getVisibleAdminMenuSections(roleContext)[0]?.links[0]?.to;
}

export function getInitialAdminModeState({
    isAdminModeEnabled,
    currentPath,
    roleContext,
}: {
    isAdminModeEnabled: boolean;
    currentPath: string;
    roleContext: AdminRoleContext;
}) {
    if (!isAdminModeEnabled) {
        return {
            isAdminModeEnabled,
            redirectPath: undefined,
        };
    }

    if (hasAdminMenuAccess(roleContext)) {
        return {
            isAdminModeEnabled: true,
            redirectPath: undefined,
        };
    }

    return {
        isAdminModeEnabled: false,
        redirectPath: ADMIN_ONLY_PATH_PREFIXES.some(
            (adminPath) => currentPath === adminPath || currentPath.startsWith(`${adminPath}/`),
        )
            ? '/'
            : undefined,
    };
}

export function canViewOrganizationProfile({ isAdminRoute, isLMS }: { isAdminRoute: boolean; isLMS: boolean }) {
    return !isAdminRoute && !isLMS;
}
